import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../../supabaseClient';
import { Task, PhysicalUnit, Level, ProjectInfo, ProtocolState, WeeklyPlan, GraphicPlan, GraphicPlanRegion } from '../../types';
import { PdfKonvaViewer, PdfRegion } from './PdfKonvaViewer';

interface GraphicProgressViewerProps {
    activeProjectId: string;
    tasks: Task[];
    units: PhysicalUnit[];
    levels: Level[];
    matrixData: Record<string, any>;
    protocolConfigs: any;
    projectInfo: ProjectInfo;
    savedProtocols: Record<string, ProtocolState>;
    weeklyPlans: WeeklyPlan[];
    selectedTaskId?: string | null;
    onClose?: () => void;
}

export const GraphicProgressViewer: React.FC<GraphicProgressViewerProps> = ({
    activeProjectId,
    tasks,
    units,
    levels,
    matrixData,
    protocolConfigs,
    projectInfo,
    savedProtocols,
    weeklyPlans,
    selectedTaskId,
    onClose
}) => {
    const [plans, setPlans] = useState<GraphicPlan[]>([]);
    const [regions, setRegions] = useState<GraphicPlanRegion[]>([]);
    const [loading, setLoading] = useState(true);
    
    const [selectedPlanId, setSelectedPlanId] = useState<string>('');

    // Fallbacks defensivos para props (prevención de nulos absolutos)
    const safeUnits = units || [];
    const safeLevels = levels || [];
    const safeTasks = tasks || [];
    const safeSavedProtocols = savedProtocols || {};
    const safeProtocolConfigs = protocolConfigs || {};
    const safeMatrixData = matrixData || {};

    const loadData = async () => {
        console.log("[Diagnostic] GraphicProgressViewer: Cargando datos para proyecto", activeProjectId);
        console.log("[Diagnostic] GraphicProgressViewer Props -> Units:", safeUnits.length, "Levels:", safeLevels.length, "Tasks:", safeTasks.length);
        setLoading(true);
        try {
            const [plansRes, regionsRes] = await Promise.all([
                supabase.from('graphic_plans').select('*').eq('project_id', activeProjectId),
                supabase.from('graphic_plan_regions').select('*').eq('project_id', activeProjectId)
            ]);
            
            if (plansRes.data) {
                setPlans(plansRes.data);
                if (plansRes.data.length > 0) setSelectedPlanId(plansRes.data[0].id);
            } else {
                console.warn("[Diagnostic] GraphicProgressViewer: plansRes.data nulo", plansRes.error);
                setPlans([]);
            }

            if (regionsRes.data) {
                setRegions(regionsRes.data);
            } else {
                console.warn("[Diagnostic] GraphicProgressViewer: regionsRes.data nulo", regionsRes.error);
                setRegions([]);
            }
        } catch (err) {
            console.error("Error loading graphic progress data", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!activeProjectId) return;
        loadData();
    }, [activeProjectId]);



    const activePlan = useMemo(() => plans.find(p => p.id === selectedPlanId), [plans, selectedPlanId]);
    const activePlanRegions = useMemo(() => regions.filter(r => r.plan_id === selectedPlanId), [regions, selectedPlanId]);

    // Lógica de cálculo de estado
    const isProtocolProgressed = (taskId: string, unitId: string) => {
        return Object.values(safeSavedProtocols).some((p: ProtocolState) => {
            if (p.taskId === taskId && p.unitId === unitId) {
                const config = safeProtocolConfigs[taskId] || projectInfo?.protocolConfigs?.[taskId];
                const group = p.groupId
                    ? config?.reviewerGroups?.find((g: any) => g.id === p.groupId)
                    : config?.reviewerGroups?.find((g: any) => g.unitIds.includes(p.unitId));

                if (group) {
                    return group.reviewers.some((rev: any, idx: number) => rev.generatesProgress && p.signatures[idx]);
                }
            }
            return false;
        });
    };

    const isUnitExecutedForTask = (taskId: string, unitId: string) => {
        const config = safeProtocolConfigs[taskId] || projectInfo?.protocolConfigs?.[taskId];
        const appliesProtocol = config?.applies ?? false;
        
        if (appliesProtocol) {
            return isProtocolProgressed(taskId, unitId);
        } else {
            return weeklyPlans?.some(plan => 
                plan.allocations?.some(alloc => alloc.taskId === taskId && alloc.unitId === unitId && alloc.executed)
            );
        }
    };



    // Mapeo a PdfRegion para Konva
    const konvaRegions: PdfRegion[] = useMemo(() => {
        return activePlanRegions.map(reg => {
            const unit = safeUnits.find(u => u?.id === reg.physical_unit_id);
            const level = safeLevels.find(l => l?.id === reg.level_id);
            const shortLabel = unit ? (level ? `${level.name?.substring(0,2)}-${unit.name}` : unit.name) : '';
            
            let color = 'rgba(203, 213, 225, 0.4)'; // slate-300 default (gris)
            
            if (selectedTaskId) {
                const cellKey = `planning_${selectedTaskId}_${reg.physical_unit_id}`;
                const hasMatrixConfig = safeMatrixData[cellKey] === '1';
                
                if (hasMatrixConfig) {
                    if (isProtocolProgressed(selectedTaskId, reg.physical_unit_id) || isUnitExecutedForTask(selectedTaskId, reg.physical_unit_id)) {
                        color = 'rgba(34, 197, 94, 0.5)'; // green-500 ejecutado
                    } else {
                        color = 'rgba(251, 191, 36, 0.4)'; // amber-400 programado/asignado pero no ejecutado
                    }
                }
            }

            return {
                id: reg.id,
                pageNumber: reg.page_number || 1,
                xPercent: reg.x_percent || 0,
                yPercent: reg.y_percent || 0,
                widthPercent: reg.width_percent || 10,
                heightPercent: reg.height_percent || 10,
                label: reg.label || shortLabel,
                color
            };
        });
    }, [activePlanRegions, selectedTaskId, safeMatrixData, safeUnits, safeLevels, safeSavedProtocols, safeProtocolConfigs, projectInfo, weeklyPlans]);



    if (loading) {
        return <div className="p-8 flex justify-center"><span className="material-symbols-outlined animate-spin text-4xl text-blue-500">sync</span></div>;
    }

    if (plans.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center h-[600px] bg-slate-50 dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-8 text-center">
                <span className="material-symbols-outlined text-6xl text-slate-300 dark:text-slate-600 mb-4">map</span>
                <h2 className="text-xl font-bold text-slate-700 dark:text-slate-200 mb-2">Sin Configuración</h2>
                <p className="text-slate-500 dark:text-slate-400 max-w-md">
                    Este proyecto aún no tiene configuración de Avance Gráfico. Dirígete a E.D.T y selecciona "Configuración de Avance Gráfico" para subir planos y mapear unidades.
                </p>
                {onClose && (
                    <button onClick={onClose} className="mt-6 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium">
                        Volver a Carta Gantt
                    </button>
                )}
            </div>
        );
    }



    return (
        <div className="flex flex-col h-full w-full bg-slate-200 dark:bg-slate-950 rounded-xl overflow-hidden relative">
            {/* Cabecera Superior Flotante */}
            <div className="absolute top-4 left-4 right-4 z-10 flex items-start justify-between pointer-events-none">
                {/* Selector de Planos */}
                <div className="bg-white/90 dark:bg-slate-900/90 backdrop-blur-md border border-slate-200 dark:border-slate-800 rounded-lg shadow-lg p-3 pointer-events-auto flex flex-col gap-2 min-w-[250px]">
                    <div className="flex items-center gap-2">
                        <span className="material-symbols-outlined text-blue-600">map</span>
                        <h3 className="font-bold text-slate-800 dark:text-white text-sm">Avance Gráfico</h3>
                    </div>
                    <select 
                        value={selectedPlanId} 
                        onChange={e => setSelectedPlanId(e.target.value)}
                        className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded p-1.5 text-xs text-slate-800 dark:text-white outline-none"
                    >
                        {plans.map(p => <option key={p.id} value={p.id}>{p.nombre_visible}</option>)}
                    </select>
                </div>

                {/* Leyenda */}
                {selectedTaskId && (
                    <div className="bg-white/90 dark:bg-slate-900/90 backdrop-blur-md border border-slate-200 dark:border-slate-800 rounded-lg shadow-lg p-3 pointer-events-auto flex flex-col gap-2 min-w-[200px]">
                        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Leyenda de Estado</div>
                        <div className="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-300">
                            <div className="w-3 h-3 rounded-sm bg-green-500 opacity-80 border border-green-600"></div> Completo
                        </div>
                        <div className="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-300">
                            <div className="w-3 h-3 rounded-sm bg-amber-400 opacity-80 border border-amber-500"></div> Avance Parcial
                        </div>
                        <div className="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-300">
                            <div className="w-3 h-3 rounded-sm bg-slate-300 opacity-60 border border-slate-400"></div> Sin Avance
                        </div>
                    </div>
                )}
            </div>

            {/* Visor Konva */}
            <div className="flex-1 w-full h-full">
                {activePlan ? (
                    <PdfKonvaViewer 
                        pdfUrl={`${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/graphic_plans/${activePlan.ruta_storage}`}
                        regions={konvaRegions}
                        interactive={false}
                    />
                ) : (
                    <div className="flex items-center justify-center h-full text-slate-400 font-medium">
                        Selecciona un plano para visualizar
                    </div>
                )}
            </div>
        </div>
    );
};
